import React, { useMemo, useState } from "react";
import clsx from "clsx";

import { Field, Label, Description, ErrorMessage } from "V2.0/common/fieldset";
import { Textarea } from "V2.0/common/textarea";
import { SwitchField, Switch } from "V2.0/common/switch";
import { Button } from "V2.0/common/button";
import { Badge } from "V2.0/common/badge";

import {
  LanguageMessage,
  SupportedLanguage,
  parseLanguageMessage,
  serializeLanguageMessage,
  languageStatuses,
  toPerLanguage,
  toSameForAll,
} from "../models/languageMessage";
import { MessageKey, MESSAGE_KEYS, MESSAGE_LABELS } from "../models/editorState";

export type LanguageSettings = {
  languageDetectionEnabled?: boolean;
  defaultLanguage?: string;
  supportedLanguages?: SupportedLanguage[];
};

type Props = {
  messages: Record<MessageKey, LanguageMessage>;
  languageSettings?: LanguageSettings | null;
  errors: Record<string, string>;
  onChange: (messages: Record<MessageKey, LanguageMessage>) => void;
};

const normalizeCode = (code: string | undefined | null) => (code ?? "").trim().toLowerCase();

export function WhatTheCallerHears({ messages, languageSettings, errors, onChange }: Props) {
  const defaultLanguage = normalizeCode(languageSettings?.defaultLanguage) || "en";
  const detectionOn = languageSettings?.languageDetectionEnabled === true;

  const supported: SupportedLanguage[] = useMemo(() => {
    const listed = (languageSettings?.supportedLanguages ?? [])
      .map((entry) => ({ code: normalizeCode(entry.code), name: (entry.name ?? "").trim() || normalizeCode(entry.code) }))
      .filter((entry) => entry.code.length > 0);
    const list = listed.length > 0 ? listed : [{ code: defaultLanguage, name: defaultLanguage === "en" ? "English" : defaultLanguage }];
    const defaultFirst = list.filter((entry) => entry.code === defaultLanguage);
    return [...defaultFirst, ...list.filter((entry) => entry.code !== defaultLanguage)];
  }, [languageSettings, defaultLanguage]);

  // Languages present in a stored message but no longer in Language Settings
  const extraCodes = useMemo(() => {
    const known = new Set(supported.map((entry) => entry.code));
    const extras: string[] = [];
    for (const key of MESSAGE_KEYS) {
      const message = messages[key];
      if (message.mode !== "perLanguage") continue;
      for (const code of message.order) if (!known.has(code) && !extras.includes(code)) extras.push(code);
    }
    return extras;
  }, [messages, supported]);

  const languageUi = detectionOn && supported.length > 1;
  const anyPerLanguage = MESSAGE_KEYS.some((key) => messages[key].mode === "perLanguage");
  const showTabs = languageUi || anyPerLanguage;

  const [activeCode, setActiveCode] = useState<string>(defaultLanguage);
  const [rawOpen, setRawOpen] = useState<Record<string, boolean>>({});
  const [rawDrafts, setRawDrafts] = useState<Record<string, string>>({});

  const defaultName = supported.find((entry) => entry.code === defaultLanguage)?.name ?? defaultLanguage;

  const filledCount = (code: string) =>
    MESSAGE_KEYS.filter((key) => {
      const message = messages[key];
      return message.mode === "same" ? (message.text ?? "").trim().length > 0 : (message.variants[code] ?? "").trim().length > 0;
    }).length;

  const update = (key: MessageKey, next: LanguageMessage) => {
    onChange({ ...messages, [key]: next });
    if (rawOpen[key]) setRawDrafts((drafts) => ({ ...drafts, [key]: serializeLanguageMessage(next, defaultLanguage) }));
  };

  const editVariant = (key: MessageKey, code: string, text: string) => {
    const message = messages[key];
    const order = message.order.includes(code) ? message.order : [...message.order, code];
    update(key, { mode: "perLanguage", text: "", variants: { ...message.variants, [code]: text }, order });
  };

  const toggleRaw = (key: MessageKey) => {
    const open = !rawOpen[key];
    setRawOpen((state) => ({ ...state, [key]: open }));
    if (open) setRawDrafts((drafts) => ({ ...drafts, [key]: serializeLanguageMessage(messages[key], defaultLanguage) }));
  };

  const editRaw = (key: MessageKey, text: string) => {
    setRawDrafts((drafts) => ({ ...drafts, [key]: text }));
    onChange({ ...messages, [key]: parseLanguageMessage(text) });
  };

  return (
    <>
      {showTabs && (
        <div className="flex flex-col gap-2">
          <div role="tablist" aria-label="Languages" className="flex flex-wrap gap-2">
            {supported.map((entry) => (
              <LanguageTab
                key={entry.code}
                name={entry.name}
                selected={activeCode === entry.code}
                isDefault={entry.code === defaultLanguage}
                count={filledCount(entry.code)}
                onSelect={() => setActiveCode(entry.code)}
              />
            ))}
            {extraCodes.map((code) => (
              <LanguageTab
                key={code}
                name={code}
                selected={activeCode === code}
                isDefault={false}
                unsupported
                count={filledCount(code)}
                onSelect={() => setActiveCode(code)}
              />
            ))}
          </div>
          <p className="text-xs text-zinc-500">
            Languages come from Language Settings.
            {!detectionOn && " Caller language detection is off, so callers hear the default language."}
          </p>
        </div>
      )}

      {MESSAGE_KEYS.map((key) => {
        const message = messages[key];
        const perLanguage = message.mode === "perLanguage";
        const label = MESSAGE_LABELS[key];
        const statuses = perLanguage ? languageStatuses(message, supported, defaultLanguage) : [];
        const error = errors[`messages.${key}`];
        return (
          <div key={key} className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-4">
            <div className="flex items-start justify-between gap-6">
              <Field className="min-w-0 flex-1">
                <Label>{label}</Label>
                {perLanguage && showTabs && (
                  <Description>Writing {supported.find((entry) => entry.code === activeCode)?.name ?? activeCode}. Switch tabs above for the other languages.</Description>
                )}
                <Textarea
                  name={`${key}-${perLanguage ? activeCode : "all"}`}
                  value={perLanguage ? message.variants[activeCode] ?? "" : message.text}
                  onChange={(event) =>
                    perLanguage ? editVariant(key, activeCode, event.target.value) : update(key, { ...message, text: event.target.value })
                  }
                  height="80px"
                  className="text-sm"
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
              </Field>
              {(languageUi || perLanguage) && (
                <SwitchField className="shrink-0 pt-1">
                  <Label>Same for every language</Label>
                  <Switch
                    color="blue"
                    checked={!perLanguage}
                    onChange={(same) => update(key, same ? toSameForAll(message, defaultLanguage) : toPerLanguage(message, defaultLanguage))}
                  />
                </SwitchField>
              )}
            </div>

            {perLanguage ? (
              <div className="flex flex-wrap gap-1.5">
                {statuses.map((status) => (
                  <Badge key={status.code} color={status.state === "filled" ? "zinc" : status.state === "empty" ? "amber" : "red"}>
                    {status.state === "filled" && status.name}
                    {status.state === "empty" && `${status.name} · empty, callers hear ${defaultName}`}
                    {status.state === "unsupported" && `${status.name} · not spoken`}
                  </Badge>
                ))}
              </div>
            ) : (
              languageUi && (
                <p className="text-xs text-zinc-500">Saved as plain text, spoken to every caller as it is. Turn the switch off to write it per language.</p>
              )
            )}

            <div className="flex flex-col gap-2">
              <Button plain className="self-start" onClick={() => toggleRaw(key)}>
                <span className="text-blue-600">{rawOpen[key] ? "Hide raw text" : "Show raw text"}</span>
              </Button>
              {rawOpen[key] && (
                <Field>
                  <Label>{`Raw text · ${label}`}</Label>
                  <Description>
                    Edits here and in the field above are the same text. Older text with a bare {"<lang>"} closer, unquoted codes or curly quotes
                    is read the same way and tidied on save, default language first.
                  </Description>
                  <Textarea
                    name={`${key}-raw`}
                    value={rawDrafts[key] ?? serializeLanguageMessage(message, defaultLanguage)}
                    onChange={(event) => editRaw(key, event.target.value)}
                    height="100px"
                    className="font-mono text-xs"
                  />
                </Field>
              )}
            </div>
          </div>
        );
      })}

      {!showTabs && (
        <p className="text-xs text-zinc-500">
          {supported.length > 1
            ? "With caller language detection off, each message has a single field."
            : "With one supported language, each message has a single field."}
        </p>
      )}
    </>
  );
}

type TabProps = {
  name: string;
  selected: boolean;
  isDefault: boolean;
  unsupported?: boolean;
  count: number;
  onSelect: () => void;
};

function LanguageTab({ name, selected, isDefault, unsupported, count, onSelect }: TabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onSelect}
      className={clsx(
        "flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm",
        selected ? "border-blue-500 bg-blue-50 text-blue-700" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"
      )}
    >
      <span className="font-medium">{name}</span>
      {isDefault && <Badge color="blue">Default</Badge>}
      {unsupported && <Badge color="red">Not in Language Settings</Badge>}
      <span className="text-xs text-zinc-500">{`${count} of ${MESSAGE_KEYS.length}`}</span>
    </button>
  );
}
